import { createSelector } from "@reduxjs/toolkit";
import { NOT_IN_PROFILE } from "src/features/review-slice";
import { ReviewMode } from "src/types";

import type { TOutlineGroup, TStyle } from "src/components";
import type { Division } from "src/components/outline/OutlineList";
import type { CharStyleFormattingOrigin } from "src/services/character_styles/types";
import type { RootState } from "src/store";
import type {
  CharacterInfo,
  CharacterStyleProfileEntry,
  OutlineSortMode,
  ParagraphInfo,
  ProfileSection,
} from "src/types";

// ── Removal group helpers ─────────────────────────────────────────────

const REMOVAL_PREFIX = "__remove:";

const REMOVAL_CHIP_CHARS: Record<string, string> = {
  italic: "i",
  bold: "b",
  underline: "u",
  strikethrough: "s",
};

export function removalGroupKey(actionDetail: string): string {
  return `${REMOVAL_PREFIX}${actionDetail}`;
}

export function isRemovalGroup(name: string): boolean {
  return name.startsWith(REMOVAL_PREFIX);
}

export function getRemovalDisplayName(
  name: string,
  characterStyles?: CharacterStyleProfileEntry[] | null
): string | null {
  if (!name.startsWith(REMOVAL_PREFIX)) return null;
  const detail = name.slice(REMOVAL_PREFIX.length);
  const normalizedDetail = detail.split(",").sort().join(",");
  const match = characterStyles?.find(
    (cs) =>
      cs.source_style !== null && cs.source_style.split(",").sort().join(",") === normalizedDetail
  );
  if (match) return `${match.full_name} (removed)`;
  const parts = detail.split(",");
  return `${parts.join(", ")} (removed)`;
}

export function getRemovalChipLabel(name: string): string | null {
  if (!name.startsWith(REMOVAL_PREFIX)) return null;
  const detail = name.slice(REMOVAL_PREFIX.length);
  const parts = detail.split(",");
  return `−${parts.map((p) => REMOVAL_CHIP_CHARS[p] ?? p.charAt(0)).join("")}`;
}

// ── Selectors ─────────────────────────────────────────────────────────

export const selectParagraphInfo = createSelector(
  [
    (state: RootState) => state.review.paragraphs,
    (state: RootState) => state.review.styleDefinitionMap,
    (state: RootState) => state.review.paragraphStyleRecords,
  ],
  (paragraphs, styleDefinitionMap, paragraphStyleRecords): ParagraphInfo[] => {
    if (!paragraphs || !styleDefinitionMap || !paragraphStyleRecords) {
      return [];
    }

    return paragraphs.map((p, i) => {
      const styleDefinition = styleDefinitionMap[p.style];
      const styleRecord = paragraphStyleRecords[i];
      return {
        index: i,
        category: styleDefinition?.category ?? NOT_IN_PROFILE,
        currentStyle: p.style,
        fullStyleName: styleDefinition?.full_name ?? p.style,
        paragraphTag: styleRecord?.paragraphTag ?? p.uniqueLocalId,
        trackedChangeCount: styleRecord?.isPending ? styleRecord?.trackedChangeCount : 0,
        uniqueLocalId: p.uniqueLocalId,
        locked: styleRecord?.locked ?? false,
      };
    });
  }
);

export const selectFilteredCharacterStyleRecords = createSelector(
  [(state: RootState) => state.review.characterStyleRecords],
  (characterStyleRecords) =>
    characterStyleRecords?.filter((record) =>
      ["apply_style", "new_style", "remove_formatting"].includes(record.actionType)
    )
);

export const selectCharacterInfo = createSelector(
  [selectFilteredCharacterStyleRecords, (state: RootState) => state.review.styleDefinitionMap],
  (characterStyleRecords, styleDefinitionMap): CharacterInfo[] => {
    if (!characterStyleRecords || !styleDefinitionMap) {
      return [];
    }

    return characterStyleRecords?.map((record, i) => {
      if (record.actionType === "remove_formatting") {
        const groupKey = removalGroupKey(record.actionDetail);
        return {
          id: record.id,
          index: i,
          category: "Removal",
          currentStyle: groupKey,
          fullStyleName: getRemovalDisplayName(groupKey) ?? "Removed formatting",
          isPending: record.isPending,
          paragraphTag: record.paragraphTag,
          trackedChangeCount: record.isPending ? record.trackedChangeCount : 0,
          paragraphUniqueLocalId: record.paragraphUniqueLocalId,
          rangeStart: record.rangeStart,
          rangeEnd: record.rangeEnd,
          formattingOrigin: record.formattingOrigin,
        };
      }
      const styleDefinition = styleDefinitionMap[record.appliedState.characterStyle ?? "NULL"];
      return {
        id: record.id,
        index: i,
        category: styleDefinition?.category ?? "None",
        currentStyle: record.appliedState.characterStyle ?? "None",
        fullStyleName: styleDefinition?.full_name ?? "None",
        isPending: record.isPending,
        paragraphTag: record.paragraphTag,
        trackedChangeCount: record.isPending ? record.trackedChangeCount : 0,
        paragraphUniqueLocalId: record.paragraphUniqueLocalId,
        rangeStart: record.rangeStart,
        rangeEnd: record.rangeEnd,
        formattingOrigin: record.formattingOrigin,
      };
    });
  }
);

export const selectFilteredParagraphInfo = createSelector(
  [
    selectParagraphInfo,
    (state: RootState) => state.review.boundaryIndices,
    (state: RootState) => state.review.ignoreParagraphs,
    (state: RootState) => state.review.reviewMode,
    (state: RootState) => state.review.selectedGroupFilter,
    (state: RootState) => state.review.selectedStyleFilter,
  ],
  (
    paragraphInfo,
    boundaryIndices,
    ignoreParagraphs,
    reviewMode,
    selectedGroupFilter,
    selectedStyleFilter
  ): ParagraphInfo[] => {
    return paragraphInfo.filter((p) => {
      let isNotIgnored = true;
      if (ignoreParagraphs) {
        isNotIgnored = p.currentStyle !== "p";
      }

      let isUnderReview = true;
      if (reviewMode === ReviewMode.ChangesOnly) {
        isUnderReview = p.trackedChangeCount > 0;
      }

      const groupIsSelected = p.category === selectedGroupFilter;
      const styleIsSelected = p.currentStyle === selectedStyleFilter;

      if (boundaryIndices) {
        const isWithinBoundaries = p.index >= boundaryIndices[0] && p.index <= boundaryIndices[1];
        if (selectedStyleFilter !== null) {
          return isWithinBoundaries && styleIsSelected && isNotIgnored && isUnderReview;
        }
        return isWithinBoundaries && isNotIgnored && isUnderReview;
      }

      if (selectedGroupFilter !== null && selectedStyleFilter !== null) {
        return groupIsSelected && styleIsSelected && isNotIgnored && isUnderReview;
      }

      if (selectedGroupFilter !== null) {
        return groupIsSelected && isNotIgnored && isUnderReview;
      }

      return isNotIgnored && isUnderReview;
    });
  }
);

export const selectFilteredCharacterInfo = createSelector(
  [
    selectCharacterInfo,
    (state: RootState) => state.review.selectedCharacterStyleFilter,
    (state: RootState) => state.review.selectedCharacterOriginFilter,
  ],
  (characterInfo, selectedCharacterStyleFilter, selectedCharacterOriginFilter): CharacterInfo[] => {
    if (selectedCharacterStyleFilter) {
      return characterInfo.filter(
        (c) =>
          c.currentStyle === selectedCharacterStyleFilter &&
          (selectedCharacterOriginFilter === null ||
            c.formattingOrigin === selectedCharacterOriginFilter)
      );
    }
    return characterInfo;
  }
);

export const selectPreviousParagraphInfo = createSelector(
  [
    selectFilteredParagraphInfo,
    selectParagraphInfo,
    (state: RootState) => state.review.selectedParagraphs,
  ],
  (filteredParagraphInfo, paragraphInfo, selectedParagraphs) => {
    const currentStartIndex = paragraphInfo.find(
      (p) => p.uniqueLocalId === selectedParagraphs?.[0]
    )?.index;
    if (currentStartIndex === undefined) return null;
    return [...filteredParagraphInfo].reverse().find((pi) => pi.index < currentStartIndex) ?? null;
  }
);

export const selectNextParagraphInfo = createSelector(
  [
    selectFilteredParagraphInfo,
    selectParagraphInfo,
    (state: RootState) => state.review.selectedParagraphs,
  ],
  (filteredParagraphInfo, paragraphInfo, selectedParagraphs) => {
    const currentEndIndex = paragraphInfo.find(
      (p) => p.uniqueLocalId === selectedParagraphs?.[selectedParagraphs.length - 1]
    )?.index;
    if (currentEndIndex === undefined) return null;
    return filteredParagraphInfo.find((pi) => pi.index > currentEndIndex) ?? null;
  }
);

export const selectPreviousCharacterInfo = createSelector(
  [
    selectCharacterInfo,
    selectFilteredCharacterInfo,
    (state: RootState) => state.review.selectedCharacterStyles,
  ],
  (characterInfo, filteredCharacterInfo, selectedCharacterStyles) => {
    const currentIndex = characterInfo.find((ci) => ci.id === selectedCharacterStyles)?.index;
    if (currentIndex === undefined) return null;
    return [...filteredCharacterInfo].reverse().find((ci) => ci.index < currentIndex) ?? null;
  }
);

export const selectNextCharacterInfo = createSelector(
  [
    selectCharacterInfo,
    selectFilteredCharacterInfo,
    (state: RootState) => state.review.selectedCharacterStyles,
  ],
  (characterInfo, filteredCharacterInfo, selectedCharacterStyles) => {
    const currentIndex = characterInfo.find((ci) => ci.id === selectedCharacterStyles)?.index;
    if (currentIndex === undefined) return null;
    return filteredCharacterInfo.find((ci) => ci.index > currentIndex) ?? null;
  }
);

export const selectSelectedIndices = createSelector(
  [
    (state: RootState) => state.review.outlineMode,
    selectCharacterInfo,
    selectParagraphInfo,
    (state: RootState) => state.review.selectedCharacterStyles,
    (state: RootState) => state.review.selectedParagraphs,
  ],
  (outlineMode, characterInfo, paragraphInfo, selectedCharacterStyles, selectedParagraphs) => {
    const selectedCharacterIndex = characterInfo
      .filter((c) => selectedCharacterStyles === c.id)
      .map((c) => c.index);
    const selectedParagraphIndices = paragraphInfo
      .filter((p) => selectedParagraphs?.includes(p.uniqueLocalId))
      .map((p) => p.index);
    return outlineMode === "character" ? selectedCharacterIndex : selectedParagraphIndices;
  }
);

// From a bare cursor in character mode the target needs a Word lookup, so the button stays enabled.
export const selectNextDisabled = createSelector(
  [
    (state: RootState) => state.review.outlineMode,
    selectFilteredCharacterInfo,
    selectSelectedIndices,
    selectNextCharacterInfo,
    selectNextParagraphInfo,
  ],
  (outlineMode, filteredCharacterInfo, selectedIndices, nextCharacterInfo, nextParagraphInfo) => {
    if (outlineMode === "character") {
      if (filteredCharacterInfo.length === 0) return true;
      return selectedIndices.length > 0 && nextCharacterInfo === null;
    }
    return nextParagraphInfo === null;
  }
);

export const selectPreviousDisabled = createSelector(
  [
    (state: RootState) => state.review.outlineMode,
    selectFilteredCharacterInfo,
    selectSelectedIndices,
    selectPreviousCharacterInfo,
    selectPreviousParagraphInfo,
  ],
  (
    outlineMode,
    filteredCharacterInfo,
    selectedIndices,
    previousCharacterInfo,
    previousParagraphInfo
  ) => {
    if (outlineMode === "character") {
      if (filteredCharacterInfo.length === 0) return true;
      return selectedIndices.length > 0 && previousCharacterInfo === null;
    }
    return previousParagraphInfo === null;
  }
);

export const selectSelectionNotInProfile = createSelector(
  [
    selectCharacterInfo,
    selectParagraphInfo,
    (state: RootState) => state.review.outlineMode,
    (state: RootState) => state.review.selectedCharacterStyles,
    (state: RootState) => state.review.selectedParagraphs,
  ],
  (characterInfo, paragraphInfo, outlineMode, selectedCharacterStyles, selectedParagraphs) => {
    if (outlineMode === "character") {
      const selected = characterInfo.find((c) => c.id === selectedCharacterStyles);
      if (!selected || isRemovalGroup(selected.currentStyle)) return false;
      return selected.category === "None";
    } else {
      return (
        selectedParagraphs?.some(
          (ulid) => paragraphInfo.find((p) => p.uniqueLocalId === ulid)?.category === NOT_IN_PROFILE
        ) ?? false
      );
    }
  }
);

export const selectNoSelectedCharacterStyle = createSelector(
  [
    (state: RootState) => state.review.selectedCharacterStyles,
    (state: RootState) => state.review.outlineMode,
  ],
  (selectedCharacterStyles, outlineMode) => outlineMode === "character" && !selectedCharacterStyles
);

export const selectChapterModeOutline = createSelector(
  [
    selectParagraphInfo,
    (state: RootState) => state.review.styleProfile,
    (state: RootState) => state.review.divisionText,
  ],
  (paragraphInfo, styleProfile, divisionText) => {
    const bookSection = styleProfile?.book_section;
    const categoryMap = bookSection?.reduce<Record<string, string>>((cm, bs) => {
      const innerMap = bs.ordered_categories.reduce<Record<string, string>>(
        (im, c) => ({ ...im, [c]: bs.name }),
        {}
      );
      return { ...cm, ...innerMap };
    }, {});

    const bodyBookSection = bookSection?.find((bs) => bs.name === "Body");
    const chapterNode = bodyBookSection?.nodes?.find((n) => n.type === "Chapter");
    const partNode = bodyBookSection?.nodes?.find((n) => n.type === "Part");

    const sections = ["Front Matter", "Body", "Back Matter"] as const;

    // When chapters carry a number, the labeling style is only ever a title
    // hanging off that number, so a stray one mid-chapter must not open a new
    // chapter. Only in an unnumbered manuscript does the title open chapters.
    const chaptersAreNumbered =
      !!chapterNode?.identifying_style &&
      paragraphInfo.some((pi) => pi.currentStyle === chapterNode.identifying_style);

    let currentSectionIndex = 0;
    let nextChapter = false;

    const groups = paragraphInfo.reduce<TOutlineGroup[]>((og, pi, i) => {
      let groups: TOutlineGroup[] = [...og];

      if (groups.length === 0) {
        const frontMatter: TOutlineGroup = {
          boundaryIndices: [0, 0],
          changes: 0,
          id: "front-matter",
          name: "Front Matter",
          styleGroup: [],
          total: 0,
        };
        const backMatter: TOutlineGroup = {
          boundaryIndices: [0, 0],
          changes: 0,
          id: "back-matter",
          name: "Back Matter",
          styleGroup: [],
          total: 0,
        };
        groups = [frontMatter, backMatter];
      }

      const isChapterNode = [chapterNode?.identifying_style, chapterNode?.labeling_style].includes(
        pi.currentStyle
      );
      const isChapterBoundary = chaptersAreNumbered
        ? chapterNode?.identifying_style === pi.currentStyle
        : isChapterNode;
      const isPartNode = partNode?.identifying_style === pi.currentStyle;
      const isBackMatterNode = categoryMap?.[pi.category] === sections[2];

      switch (sections[currentSectionIndex]) {
        case "Front Matter": {
          if (isChapterBoundary || isPartNode) {
            currentSectionIndex += 1;
            nextChapter = true;
          } else {
            const frontMatterGroup = groups[0];
            const styleGroup = frontMatterGroup.styleGroup.find(
              (sg) => sg.name === pi.currentStyle
            );
            if (frontMatterGroup.boundaryIndices) {
              frontMatterGroup.boundaryIndices[1] = i;
            }
            frontMatterGroup.changes += pi.trackedChangeCount;
            frontMatterGroup.total += 1;
            if (styleGroup) {
              styleGroup.changes += pi.trackedChangeCount;
              styleGroup.total += 1;
            } else {
              frontMatterGroup.styleGroup = [
                ...frontMatterGroup.styleGroup,
                {
                  changes: pi.trackedChangeCount,
                  name: pi.currentStyle,
                  total: 1,
                },
              ];
            }
            break;
          }
        }
        case "Body": {
          if (isBackMatterNode) {
            const backMatterGroup = groups[groups.length - 1];
            if (backMatterGroup.boundaryIndices) {
              backMatterGroup.boundaryIndices[0] = i;
            }
            currentSectionIndex += 1;
          } else {
            if (nextChapter && (isChapterBoundary || isPartNode)) {
              let division: Division = {
                boundaryIndices: [i, 0],
              };
              if (pi.currentStyle === chapterNode?.identifying_style) {
                division = {
                  ...division,
                  chapter: { number: divisionText?.[pi.uniqueLocalId] },
                };
              }
              if (pi.currentStyle === chapterNode?.labeling_style) {
                division = {
                  ...division,
                  chapter: { title: divisionText?.[pi.uniqueLocalId] },
                };
              }
              if (pi.currentStyle === partNode?.identifying_style) {
                division = {
                  ...division,
                  part: { number: divisionText?.[pi.uniqueLocalId] },
                };
              }

              const bodyGroup: TOutlineGroup = {
                changes: pi.trackedChangeCount,
                id: `body-${i}`,
                name: "Body",
                styleGroup: [{ changes: pi.trackedChangeCount, name: pi.currentStyle, total: 1 }],
                total: 1,
                ...division,
              };

              const insertionIndex = Math.max(0, groups.length - 1);
              groups.splice(insertionIndex, 0, bodyGroup);
              nextChapter = false;
            } else {
              const group = groups[groups.length - 2];
              group.changes += pi.trackedChangeCount;
              group.total += 1;

              if (pi.currentStyle === chapterNode?.identifying_style && group.chapter) {
                group.chapter.number = divisionText?.[pi.uniqueLocalId];
              }
              if (pi.currentStyle === chapterNode?.labeling_style && group.chapter) {
                group.chapter.title = divisionText?.[pi.uniqueLocalId];
              }
              if (pi.currentStyle === partNode?.identifying_style && group.part) {
                group.part.number = divisionText?.[pi.uniqueLocalId];
              }

              const styleGroup = group.styleGroup.find((sg) => sg.name === pi.currentStyle);
              if (styleGroup) {
                styleGroup.changes += pi.trackedChangeCount;
                styleGroup.total += 1;
              } else {
                group.styleGroup = [
                  ...group.styleGroup,
                  {
                    changes: pi.trackedChangeCount,
                    name: pi.currentStyle,
                    total: 1,
                  },
                ];
              }

              if (!isChapterNode && !isPartNode) {
                if (group.boundaryIndices) {
                  group.boundaryIndices[1] = i;
                }
                nextChapter = true;
              }
            }
            break;
          }
        }
        case "Back Matter": {
          const backMatterGroup = groups[groups.length - 1];
          if (backMatterGroup.boundaryIndices) {
            backMatterGroup.boundaryIndices[1] = i;
          }
          backMatterGroup.changes += pi.trackedChangeCount;
          backMatterGroup.total += 1;

          const styleGroup = backMatterGroup.styleGroup.find((sg) => sg.name === pi.currentStyle);
          if (styleGroup) {
            styleGroup.changes += pi.trackedChangeCount;
            styleGroup.total += 1;
          } else {
            backMatterGroup.styleGroup = [
              ...backMatterGroup.styleGroup,
              {
                changes: pi.trackedChangeCount,
                name: pi.currentStyle,
                total: 1,
              },
            ];
          }
          break;
        }
      }
      return groups;
    }, []);

    const backMatter = groups[groups.length - 1];
    if (backMatter?.styleGroup.length === 0) {
      return groups.slice(0, -1);
    }

    return groups;
  }
);

export const selectStyleModeOutline = createSelector([selectParagraphInfo], (paragraphInfo) => {
  const groups = paragraphInfo.reduce<TOutlineGroup[]>((acc, p, i) => {
    const group = acc.find((g) => g.name === p.category);
    if (group) {
      group.changes = group.changes + p.trackedChangeCount;
      group.total = group.total + 1;
      const style = group.styleGroup.find((sg) => sg.name === p.currentStyle);
      if (style) {
        style.changes = style.changes + p.trackedChangeCount;
        style.total = style.total + 1;
      } else {
        const style: TStyle = {
          changes: p.trackedChangeCount,
          name: p.currentStyle,
          total: 1,
        };
        group.styleGroup = [...group.styleGroup, style];
      }
      return acc;
    } else {
      const group: TOutlineGroup = {
        changes: p.trackedChangeCount,
        id: `style-detail-${i}`,
        name: p.category,
        total: 1,
        styleGroup: [
          {
            changes: p.trackedChangeCount,
            name: p.currentStyle,
            total: 1,
          },
        ],
      };
      return [...acc, group];
    }
  }, []);

  // "Not in Profile" always sorts to the top so unmapped styles are surfaced first.
  return groups.sort((a, b) => {
    if (a.name === NOT_IN_PROFILE) return -1;
    if (b.name === NOT_IN_PROFILE) return 1;
    return 0;
  });
});

export const selectLockedParagraphIds = createSelector(
  [selectParagraphInfo],
  (paragraphInfo): Set<string> =>
    new Set(paragraphInfo.filter((p) => p.locked).map((p) => p.uniqueLocalId))
);

export const selectIsMultiselect = createSelector(
  [selectSelectedIndices],
  (selectedIndices) => selectedIndices.length > 1
);

export const selectSelectionBounds = createSelector(
  [
    (state: RootState) => state.review.outlineMode,
    selectFilteredCharacterInfo,
    selectFilteredParagraphInfo,
    selectIsMultiselect,
    selectSelectedIndices,
  ],
  (
    outlineMode,
    filteredCharacterInfo,
    filteredParagraphInfo,
    isMultiSelect,
    selectedIndices
  ): [number, number?] => {
    if (selectedIndices.length === 0) return [0];

    if (outlineMode === "character") {
      const selectedIndex = filteredCharacterInfo.findIndex((c) => c.index === selectedIndices[0]);

      if (selectedIndex === -1) {
        return [0];
      }

      return [selectedIndex];
    } else {
      if (isMultiSelect) {
        return [selectedIndices[0], selectedIndices[selectedIndices.length - 1]];
      }

      const selectedIndex = filteredParagraphInfo.findIndex((p) => p.index === selectedIndices[0]);

      if (selectedIndex === -1) {
        return [0];
      }

      return [selectedIndex];
    }
  }
);

export const selectSelectedItem = createSelector(
  [
    selectCharacterInfo,
    selectParagraphInfo,
    (state: RootState) => state.review.selectedCharacterStyles,
    (state: RootState) => state.review.selectedParagraphs,
    (state: RootState) => state.review.outlineMode,
  ],
  (
    characterInfo,
    paragraphInfo,
    selectedCharacterStyles,
    selectedParagraphs,
    outlineMode
  ): ParagraphInfo | CharacterInfo | null => {
    const selectedCharacterStyle = characterInfo.find((c) => selectedCharacterStyles === c.id);
    const selectedParagraph = paragraphInfo.find((p) =>
      selectedParagraphs?.includes(p.uniqueLocalId)
    );
    return (outlineMode === "character" ? selectedCharacterStyle : selectedParagraph) ?? null;
  }
);

export const selectTotal = createSelector(
  [
    (state: RootState) => state.review.outlineMode,
    selectFilteredCharacterInfo,
    selectIsMultiselect,
    selectParagraphInfo,
    selectFilteredParagraphInfo,
  ],
  (outlineMode, filteredCharacterInfo, isMultiSelect, paragraphInfo, filteredParagraphInfo) => {
    const characterModeTotal = filteredCharacterInfo.length;
    const paragraphModeTotal = isMultiSelect ? paragraphInfo.length : filteredParagraphInfo.length;
    return outlineMode === "character" ? characterModeTotal : paragraphModeTotal;
  }
);

export const selectSelectedItemTracked = createSelector([selectSelectedItem], (selectedItem) => {
  if (selectedItem) {
    if ("isPending" in selectedItem) {
      return selectedItem.isPending ? selectedItem.trackedChangeCount : 0;
    } else {
      return selectedItem.trackedChangeCount;
    }
  }
  return 0;
});

export const selectFilteredTotalTracked = createSelector(
  [
    (state: RootState) => state.review.outlineMode,
    selectFilteredCharacterInfo,
    selectFilteredParagraphInfo,
  ],
  (outlineMode, filteredCharacterInfo, filteredParagraphInfo) => {
    if (outlineMode === "character") {
      return filteredCharacterInfo.reduce((acc, c) => (acc += c.trackedChangeCount), 0);
    } else {
      return filteredParagraphInfo.reduce((acc, p) => (acc += p.trackedChangeCount), 0);
    }
  }
);

export const selectDocumentSummaryCounts = createSelector(
  [selectParagraphInfo],
  (paragraphInfo) => {
    const styles = new Set<string>();
    const categories = new Set<string>();

    for (const p of paragraphInfo) {
      styles.add(p.currentStyle);
      categories.add(p.category);
    }

    return {
      styleCount: styles.size,
      categoryCount: categories.size,
      paragraphCount: paragraphInfo.length,
    };
  }
);

export const FORMATTING_ORIGIN_GROUPS: ReadonlyArray<{
  origin: CharStyleFormattingOrigin;
  name: string;
}> = [
  { origin: "source", name: "Word Source Formatted" },
  { origin: "inference", name: "Inference Formatted" },
  { origin: "restyled", name: "Restyled" },
];

export type CharacterOutlineGroup = TOutlineGroup & { origin: CharStyleFormattingOrigin };

export const selectCharacterModeOutline = createSelector(
  [selectFilteredCharacterStyleRecords],
  (characterStyleRecords): CharacterOutlineGroup[] => {
    if (!characterStyleRecords) return [];
    const styleMaps = new Map<CharStyleFormattingOrigin, Map<string, TStyle>>();
    for (const csr of characterStyleRecords) {
      const styleName =
        csr.actionType === "remove_formatting"
          ? removalGroupKey(csr.actionDetail)
          : (csr.appliedState.characterStyle ?? "None");
      const changes = csr.isPending ? csr.trackedChangeCount : 0;

      let styleMap = styleMaps.get(csr.formattingOrigin);
      if (!styleMap) {
        styleMap = new Map();
        styleMaps.set(csr.formattingOrigin, styleMap);
      }
      const existing = styleMap.get(styleName);
      if (existing) {
        existing.changes += changes;
        existing.total += 1;
      } else {
        styleMap.set(styleName, { changes, name: styleName, total: 1 });
      }
    }

    return FORMATTING_ORIGIN_GROUPS.flatMap(({ origin, name }) => {
      const styleMap = styleMaps.get(origin);
      if (!styleMap) return [];
      const styleGroup = Array.from(styleMap.values());
      return [
        {
          id: `character-origin-${origin}`,
          name,
          origin,
          styleGroup,
          changes: styleGroup.reduce((acc, sg) => acc + sg.changes, 0),
          total: styleGroup.reduce((acc, sg) => acc + sg.total, 0),
        },
      ];
    });
  }
);

export function sortOutlineGroups(
  groups: TOutlineGroup[],
  sortMode: OutlineSortMode,
  bookSections?: ProfileSection[]
): TOutlineGroup[] {
  const sorted = [...groups];

  if (sortMode === "book_section") {
    if (!bookSections || bookSections.length === 0) {
      return sorted;
    }
    const orderMap = new Map<string, number>();
    let idx = 0;
    for (const section of bookSections) {
      for (const category of section.ordered_categories) {
        if (!orderMap.has(category)) {
          orderMap.set(category, idx++);
        }
      }
    }
    const hasMatchingEntries = sorted.some((g) => orderMap.has(g.name));
    if (!hasMatchingEntries) {
      return sorted;
    }
    sorted.sort((a, b) => {
      if (a.name === NOT_IN_PROFILE) return -1;
      if (b.name === NOT_IN_PROFILE) return 1;
      const aOrder = orderMap.get(a.name) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = orderMap.get(b.name) ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder || a.name.localeCompare(b.name);
    });
    return sorted;
  }

  sorted.sort((a, b) => {
    if (a.name === NOT_IN_PROFILE) return -1;
    if (b.name === NOT_IN_PROFILE) return 1;

    if (sortMode === "count-asc") {
      return a.total - b.total || a.name.localeCompare(b.name);
    }
    return b.total - a.total || a.name.localeCompare(b.name);
  });

  return sorted.map((g) => ({
    ...g,
    styleGroup: [...g.styleGroup].sort((a, b) =>
      sortMode === "count-asc"
        ? a.total - b.total || a.name.localeCompare(b.name)
        : b.total - a.total || a.name.localeCompare(b.name)
    ),
  }));
}

export const selectOutlineSortMode = (state: RootState) => state.review.outlineSortMode;

const selectBookSections = (state: RootState) => state.review.styleProfile?.book_section;

const selectOutlineMode = (state: RootState) => state.review.outlineMode;

export const selectSortedOutline = createSelector(
  [
    selectStyleModeOutline,
    selectChapterModeOutline,
    selectOutlineMode,
    selectOutlineSortMode,
    selectBookSections,
  ],
  (styleModeGroups, chapterModeGroups, outlineMode, sortMode, bookSections) => {
    if (outlineMode === "chapter") {
      return chapterModeGroups;
    }
    if (outlineMode === "style") {
      return sortOutlineGroups(styleModeGroups, sortMode, bookSections);
    }
    return [];
  }
);
